import { DialogComponent } from '@theme/dialog';
import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';

// Type definitions for better IDE support
/**
 * @typedef {object} ProductData
 * @property {string} handle
 * @property {string[]} options
 * @property {Variant[]} variants
 */

/**
 * @typedef {object} Variant
 * @property {number} id
 * @property {string[]} options
 * @property {boolean} available
 */

/**
 * @typedef {object} SizeValue
 * @property {string} value
 * @property {number} variantId
 * @property {boolean} available
 */

/**
 * @typedef {object} LineItem
 * @property {string} productHandle
 * @property {number} variantId
 * @property {string} lineKey
 */

/**
 * @typedef {object} CacheEntry
 * @property {ProductData} data
 * @property {number} timestamp
 */

/**
 * @typedef {object} OpenConfig
 * @property {string} productHandle
 * @property {number|string} variantId
 * @property {string} lineKey
 * @property {Object.<string, any>} [properties]
 */

/**
 * A custom element that manages the size selector modal for cart quantity increments.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element
 * @property {HTMLElement} customBackdrop - The custom backdrop element (iOS Safari fix)
 * @property {HTMLElement} modalTitle - The modal title element
 * @property {HTMLElement} modalDescription - The modal description element
 * @property {HTMLElement} loadingState - The loading state container
 * @property {HTMLElement} sizesContainer - The sizes container
 * @property {HTMLElement} errorState - The error state container
 * @property {HTMLElement} errorMessage - The error message element
 * @property {HTMLButtonElement} closeButton - The close button
 *
 * @extends {DialogComponent}
 */
class SizeSelectorModal extends DialogComponent {
  // Private instance variables
  /** @type {LineItem|null} */
  #currentLineItem = null;

  /** @type {ProductData|null} */
  #productData = null;

  /** @type {Object.<string, any>|null} */
  #originalProperties = null;

  /** @type {AbortController|null} */
  #abortController = null;

  /** @type {boolean} */
  #isProcessing = false;

  /** @type {Map<HTMLButtonElement, Function>} */
  #buttonClickHandlers = new Map();

  /** @type {boolean} */
  #isSafari = false;

  // Static product cache (shared across all instances)
  /** @type {Map<string, CacheEntry>} */
  static #productCache = new Map();

  /** @type {number} */
  static #CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /** @type {number} */
  static #DEBOUNCE_DELAY = 300; // 300ms debounce

  /**
   * Called when component is connected to DOM
   */
  connectedCallback() {
    super.connectedCallback();

    // Detect Safari browser
    this.#isSafari = this.#detectSafari();

    // Add class to element for Safari-specific styles
    if (this.#isSafari) {
      this.classList.add('is-safari');
    }
  }

  /**
   * Detects if the browser is Safari
   * @returns {boolean}
   */
  #detectSafari() {
    const ua = navigator.userAgent.toLowerCase();
    // Check for Safari (but not Chrome, which also includes Safari in UA)
    return ua.indexOf('safari') !== -1 && ua.indexOf('chrome') === -1;
  }

  /**
   * Override showDialog - simplified like sizeguide-table-modal
   */
  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    dialog.showModal();
    if (this.refs.customBackdrop) {
      this.refs.customBackdrop.classList.add('active');
    }
    document.body.style.overflow = 'hidden';
  }

  /**
   * Override closeDialog - simplified like sizeguide-table-modal
   */
  async closeDialog() {
    const { dialog } = this.refs;

    // Always remove backdrop and restore scroll, even if dialog isn't open
    if (this.refs.customBackdrop) {
      this.refs.customBackdrop.classList.remove('active');
    }
    document.body.style.overflow = '';

    if (!dialog.open) return;

    dialog.classList.add('dialog-closing');

    // Wait for animation
    await new Promise((resolve) => setTimeout(resolve, 150));

    dialog.close();
    dialog.classList.remove('dialog-closing');
  }

  /**
   * Opens the modal with product and line item data
   * @param {OpenConfig} config - Configuration object
   * @returns {Promise<void>}
   */
  async open(config) {
    const { productHandle, variantId, lineKey, properties } = config;

    // Convert variantId to number for consistency
    const variantIdNumber = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;

    this.#currentLineItem = { productHandle, variantId: variantIdNumber, lineKey };
    this.#originalProperties = properties || {};

    // Cancel any pending requests
    this.#cancelPendingRequests();

    // Show loading state
    this.#showLoading();

    // Open the modal
    this.showDialog();

    // Create new abort controller for this request
    this.#abortController = new AbortController();

    try {
      // Fetch product data (with caching)
      const product = await this.#fetchProductData(productHandle, this.#abortController.signal);
      this.#productData = product;

      // Find the size option - support multiple languages and variations
      const sizeKeywords = ['size', 'maat', 'größe', 'grosse', 'grösse', 'taille', 'talla', 'tamanho', 'dimensione'];

      // Normalize option to string (handle both string and object with 'name' property)
      const getOptionName = (opt) => {
        if (!opt) return '';
        if (typeof opt === 'string') return opt;
        if (typeof opt === 'object' && opt.name) return opt.name;
        return '';
      };

      const sizeOptionIndex = product.options.findIndex((opt) => {
        const optionName = getOptionName(opt);
        if (!optionName) return false;
        const normalized = optionName.toLowerCase().trim();
        return sizeKeywords.some((keyword) => normalized === keyword);
      });

      if (sizeOptionIndex === -1) {
        const availableOptions = product.options.map((opt) => getOptionName(opt)).filter((name) => name).join(', ');
        throw new Error(`Size option not found. Available options: ${availableOptions || 'none'}`);
      }

      // Get all size values from variants
      const sizeValues = this.#getSizeValues(product, sizeOptionIndex);

      // Render size buttons
      this.#renderSizes(sizeValues, sizeOptionIndex);

      // Hide loading, show sizes
      this.#hideLoading();
    } catch (error) {
      // Don't show error if request was aborted (user closed modal)
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      const errorMessage = error instanceof Error ? error.message : 'Failed to load sizes';
      this.#showError(errorMessage);
    }
  }

  /**
   * Closes the modal and resets state
   * @returns {void}
   */
  close() {
    this.#cancelPendingRequests();
    // Always remove backdrop active class
    if (this.refs.customBackdrop) {
      this.refs.customBackdrop.classList.remove('active');
    }
    document.body.style.overflow = '';
    this.closeDialog();
    this.#resetState();
  }

  /**
   * Cancels any pending fetch requests
   * @returns {void}
   */
  #cancelPendingRequests() {
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
  }

  /**
   * Fetches product data from Shopify API with caching
   * @param {string} handle - The product handle
   * @param {AbortSignal} signal - Abort signal for cancellation
   * @returns {Promise<ProductData>} The product data
   */
  async #fetchProductData(handle, signal) {
    // Check cache first
    const cached = SizeSelectorModal.#productCache.get(handle);
    if (cached && Date.now() - cached.timestamp < SizeSelectorModal.#CACHE_DURATION) {
      return cached.data;
    }

    // Fetch from API
    const response = await fetch(`/products/${handle}.js`, { signal });
    if (!response.ok) {
      throw new Error('Failed to fetch product data');
    }
    const data = await response.json();

    // Store in cache
    SizeSelectorModal.#productCache.set(handle, {
      data,
      timestamp: Date.now(),
    });

    return data;
  }

  /**
   * Gets unique size values from product variants (optimized for large variant counts)
   * @param {ProductData} product - The product data
   * @param {number} sizeOptionIndex - The index of the size option
   * @returns {SizeValue[]}
   */
  #getSizeValues(product, sizeOptionIndex) {
    // Ensure we have current line item
    if (!this.#currentLineItem) {
      return [];
    }

    // Find current variant
    const currentVariant = product.variants.find((v) => v.id === this.#currentLineItem.variantId);

    if (!currentVariant) {
      return [];
    }

    // Build a signature of non-size options for comparison
    const currentSignature = currentVariant.options
      .map((opt, idx) => (idx === sizeOptionIndex ? null : opt))
      .filter((opt) => opt !== null)
      .join('|');

    // Single pass through variants - O(n) instead of O(n²)
    const sizeMap = new Map();

    for (const variant of product.variants) {
      // Build signature for this variant
      const variantSignature = variant.options
        .map((opt, idx) => (idx === sizeOptionIndex ? null : opt))
        .filter((opt) => opt !== null)
        .join('|');

      // Only include variants with matching non-size options
      if (variantSignature === currentSignature) {
        const sizeValue = variant.options[sizeOptionIndex];

        // Store first occurrence of each size
        if (!sizeMap.has(sizeValue)) {
          sizeMap.set(sizeValue, {
            value: sizeValue,
            variantId: variant.id,
            available: variant.available,
          });
        }
      }
    }

    return Array.from(sizeMap.values());
  }

  /**
   * Renders size option buttons with proper cleanup
   * @param {SizeValue[]} sizes - Array of size objects
   * @param {number} sizeOptionIndex - The index of the size option
   * @returns {void}
   */
  #renderSizes(sizes, sizeOptionIndex) {
    const { sizesContainer } = this.refs;

    // Clean up old event listeners
    this.#cleanupEventListeners();

    // Clear container
    sizesContainer.innerHTML = '';

    sizes.forEach((size) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'size-option-button';
      button.textContent = size.value;
      // Convert number to string for dataset
      button.dataset.variantId = String(size.variantId);

      // Note: Not disabling unavailable sizes per user requirement
      // Just showing all sizes without stock info

      // Create debounced handler
      const handler = this.#createDebouncedHandler(() => this.#handleSizeSelection(size.variantId));

      // Store handler for cleanup
      this.#buttonClickHandlers.set(button, handler);

      button.addEventListener('click', handler);

      sizesContainer.appendChild(button);
    });

    sizesContainer.classList.add('loaded');
  }

  /**
   * Creates a debounced event handler
   * @param {Function} fn - The function to debounce
   * @returns {EventListener} The debounced function
   */
  #createDebouncedHandler(fn) {
    /** @type {number|null} */
    let timeoutId = null;

    return function debouncedHandler(...args) {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        fn.apply(this, args);
        timeoutId = null;
      }, SizeSelectorModal.#DEBOUNCE_DELAY);
    };
  }

  /**
   * Cleans up event listeners from previous renders
   * @returns {void}
   */
  #cleanupEventListeners() {
    this.#buttonClickHandlers.forEach((handler, button) => {
      button.removeEventListener('click', handler);
    });
    this.#buttonClickHandlers.clear();
  }

  /**
   * Handles size selection and adds to cart (with duplicate prevention)
   * @param {number} variantId - The selected variant ID
   * @returns {Promise<void>}
   */
  async #handleSizeSelection(variantId) {
    // Prevent duplicate submissions
    if (this.#isProcessing) {
      return;
    }

    this.#isProcessing = true;

    try {
      // Disable all buttons during processing
      const buttons = this.refs.sizesContainer.querySelectorAll('button');
      buttons.forEach((btn) => (btn.disabled = true));

      // Show loading state
      this.#showLoading();

      // Add the new variant to cart with same properties
      await this.#addToCart(variantId);

      // Check if there's a TeeInBlue addon variant that needs to be added
      const addonVariantId = this.#extractAddonVariantId();
      console.log('Addon variant ID extracted:', addonVariantId);
      if (addonVariantId) {
        console.log('Adding addon to cart with variant ID:', addonVariantId);
        await this.#addAddonToCart(addonVariantId);
        console.log('Addon successfully added to cart');
      } else {
        console.log('No addon variant to add');
      }

      // Poll cart and dispatch updates for TeeInBlue integration
      // This allows TeeInBlue to detect the cart change and add customization addons
      // Note: We don't wait for exact item count since we're incrementing existing addon
      await this.#pollAndUpdateCart(1);

      // Close modal after TeeInBlue has finished adding addons
      this.close();
    } catch (error) {
      console.error('Error adding to cart:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add item to cart';
      this.#showError(errorMessage);

      // Re-enable buttons
      const buttons = this.refs.sizesContainer.querySelectorAll('button');
      buttons.forEach((btn) => (btn.disabled = false));
    } finally {
      this.#isProcessing = false;
    }
  }

  /**
   * Extracts TeeInBlue addon variant ID from original properties
   * @returns {number|null} The addon variant ID or null if not found
   */
  #extractAddonVariantId() {
    console.log('Extracting addon variant from properties:', this.#originalProperties);

    if (!this.#originalProperties) {
      console.log('No original properties found');
      return null;
    }

    // Properties can be in multiple formats:
    // 1. Object: { "_tib_addon_variant": "55137774502153:1" }
    // 2. Object with numeric keys: { "2": "_tib_addon_variant,55137774502153:1" }
    // 3. Array of arrays: [["_tib_addon_variant", "55137774502153:1"], ...]

    // Check if properties is an array
    if (Array.isArray(this.#originalProperties)) {
      console.log('Properties is an array, checking each item...');
      for (const item of this.#originalProperties) {
        if (Array.isArray(item) && item.length >= 2) {
          const [key, value] = item;
          console.log(`Array property [${key}]:`, value);

          // Check if key is "_tib_addon_variant"
          // Format: "variantId:quantity" e.g., "55137774502153:1"
          if (key === '_tib_addon_variant') {
            const variantId = value.split(':')[0]; // Extract variantId from "variantId:quantity"
            console.log('Found addon variant in array format - variantId:', variantId, 'from value:', value);
            const parsedId = parseInt(variantId, 10);
            console.log('Parsed variant ID:', parsedId);
            return parsedId;
          }

          // Check if value contains "_tib_addon_variant,"
          if (typeof value === 'string' && value.includes('_tib_addon_variant,')) {
            const parts = value.split(',');
            if (parts.length >= 2) {
              const variantId = parts[1].split(':')[0];
              console.log('Found addon variant in TeeInBlue array format:', variantId);
              return parseInt(variantId, 10);
            }
          }
        }
      }
    } else {
      // Handle object format
      console.log('Properties is an object, checking keys...');

      // Check standard format first
      const standardAddon = this.#originalProperties['_tib_addon_variant'];
      console.log('Standard addon format check:', standardAddon);
      if (standardAddon) {
        const variantId = standardAddon.split(':')[0];
        console.log('Extracted standard addon variant ID:', variantId);
        return parseInt(variantId, 10);
      }

      // Check TeeInBlue numeric format
      for (const [key, value] of Object.entries(this.#originalProperties)) {
        console.log(`Object property ${key}:`, value);
        if (typeof value === 'string' && value.includes('_tib_addon_variant')) {
          console.log('Found _tib_addon_variant in value:', value);
          const parts = value.split(',');
          if (parts.length >= 2) {
            const variantData = parts[1].split(':')[0];
            console.log('Extracted TeeInBlue numeric addon variant ID:', variantData);
            return parseInt(variantData, 10);
          }
        }
      }
    }

    console.log('No addon variant found');
    return null;
  }

  /**
   * Normalizes properties from TeeInBlue format to standard Shopify format
   * TeeInBlue format: {"0": "_customization_id,value", "5": "Kleur draad,White - Snow"}
   * Shopify format: {"_customization_id": "value", "Kleur draad": "White - Snow"}
   * @param {Object.<string, any>|Array} properties - The properties object or array
   * @returns {Object.<string, string>} Normalized properties
   */
  #normalizeProperties(properties) {
    if (!properties) return {};

    const normalized = {};

    // Handle array format: [["key", "value"], ...]
    if (Array.isArray(properties)) {
      for (const item of properties) {
        if (Array.isArray(item) && item.length >= 2) {
          const [key, value] = item;
          // Standard array format - add as is
          normalized[String(key)] = String(value);
        }
      }
      return normalized;
    }

    // Handle object format
    for (const [key, value] of Object.entries(properties)) {
      // Check if this is TeeInBlue format (numeric key + comma in value)
      if (/^\d+$/.test(key) && typeof value === 'string' && value.includes(',')) {
        // Split by comma: first part is property name, rest is property value
        const commaIndex = value.indexOf(',');
        const propName = value.substring(0, commaIndex).trim();
        const propValue = value.substring(commaIndex + 1).trim();

        // Add with the parsed property name as the key
        normalized[propName] = propValue;
      } else {
        // Standard format - keep as is
        normalized[key] = value;
      }
    }

    return normalized;
  }

  /**
   * Finds an existing addon item in the cart with the same variant ID
   * @param {Object} cart - The cart object
   * @param {number} addonVariantId - The addon variant ID to find
   * @returns {Object|null} The cart item or null if not found
   */
  #findAddonInCart(cart, addonVariantId) {
    if (!cart.items) return null;

    const addon = cart.items.find((item) => {
      return item.variant_id === addonVariantId;
    });

    if (addon) {
      console.log('Found addon in cart:', {
        key: addon.key,
        variant_id: addon.variant_id,
        quantity: addon.quantity,
        can_update_quantity: addon.instructions?.can_update_quantity,
        can_remove: addon.instructions?.can_remove,
        product_type: addon.product_type,
      });
    }

    return addon;
  }

  /**
   * Increments the quantity of an existing addon or adds it to cart
   * @param {number} addonVariantId - The addon variant ID to add
   * @returns {Promise<void>}
   */
  async #addAddonToCart(addonVariantId) {
    // First, fetch the current cart to check if addon already exists
    const cart = await this.#fetchFreshCart();
    const existingAddon = this.#findAddonInCart(cart, addonVariantId);

    if (existingAddon) {
      console.log('Addon already exists in cart, incrementing quantity. Current quantity:', existingAddon.quantity);

      // Check if quantity updates are allowed
      if (existingAddon.instructions?.can_update_quantity === false) {
        console.warn('Addon has quantity updates disabled, will add new item instead');
        // Fall through to add new item logic by setting existingAddon to null
        // But we can't do that here, so we just skip the increment
        console.log('Skipping addon increment - quantity updates disabled for this product');
        return;
      }

      // Increment the quantity of the existing addon using cart change endpoint
      const newQuantity = existingAddon.quantity + 1;
      const body = JSON.stringify({
        id: existingAddon.key,
        quantity: newQuantity,
      });

      console.log('Sending cart change request with body:', body);
      console.log('Using cart change URL:', Theme.routes.cart_change_url);

      const response = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: body,
      });

      console.log('Cart change response status:', response.status, response.ok);

      const data = await response.json();
      console.log('Cart change response data:', data);

      if (data.status || !response.ok) {
        console.error('Cart change failed:', data);
        throw new Error(data.message || data.description || 'Failed to update addon quantity');
      }

      // The response is the full cart object, find the updated item
      const updatedItem = data.items?.find((item) => item.key === existingAddon.key);
      console.log('Addon quantity incremented successfully. New quantity:', newQuantity);
      console.log('Updated item from cart:', updatedItem);
      console.log('Updated item quantity:', updatedItem?.quantity);

      // Dispatch cart update event so the UI refreshes
      this.#dispatchCartUpdateEvent(data, 1);
      console.log('Cart update event dispatched after addon increment');
    } else {
      console.log('Addon does not exist in cart, adding new item');

      // Add new addon to cart
      const formData = new FormData();
      formData.append('id', String(addonVariantId));
      formData.append('quantity', '1');

      // Normalize and copy all original properties to the addon as well
      if (this.#originalProperties) {
        const normalizedProps = this.#normalizeProperties(this.#originalProperties);
        Object.entries(normalizedProps).forEach(([key, value]) => {
          formData.append(`properties[${key}]`, String(value));
        });
      }

      const fetchCfg = fetchConfig('javascript', { body: formData });

      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
        },
      });

      const data = await response.json();

      if (data.status || !response.ok) {
        throw new Error(data.message || data.description || 'Failed to add addon item');
      }

      console.log('New addon added to cart successfully');
    }
  }

  /**
   * Fetches fresh cart data from Shopify
   * @returns {Promise<Object>} The cart object with items and count
   */
  async #fetchFreshCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      const cart = await response.json();
      return cart;
    } catch (error) {
      return { items: [], item_count: 0 };
    }
  }

  /**
   * Dispatches cart:update custom event for TeeInBlue integration
   * @param {Object} cartResponse - The cart response from Shopify
   * @param {number} itemCount - Number of items added
   * @returns {void}
   */
  #dispatchCartUpdateEvent(cartResponse, itemCount) {
    const cartUpdateEvent = new CustomEvent('cart:update', {
      detail: {
        resource: cartResponse,
        sourceId: 'size-selector-modal',
        data: {
          itemCount: itemCount,
          source: 'size-selector-modal',
          sections: cartResponse.sections || {},
        },
      },
      bubbles: true,
    });

    // Dispatch on both document and window for maximum compatibility
    document.dispatchEvent(cartUpdateEvent);
    window.dispatchEvent(cartUpdateEvent);
  }

  /**
   * Polls cart and dispatches updates until TeeInBlue addons are added
   * @param {number} expectedBaseItems - Expected number of base items (usually 1)
   * @returns {Promise<void>}
   */
  async #pollAndUpdateCart(expectedBaseItems) {
    return new Promise(async (resolve) => {
      let attempts = 0;
      const maxAttempts = 60; // 12 seconds max (60 × 200ms)
      let lastItemCount = 0;
      let stableChecks = 0;

      // Calculate expected items including TeeInBlue addons
      const addonCount = window.teeinblueCampaign?.addonVariants?.length || 0;
      const expectedItemCount = expectedBaseItems + addonCount * expectedBaseItems;

      const poll = async () => {
        attempts++;
        const cart = await this.#fetchFreshCart();

        // Dispatch cart update on every check so drawer/page refreshes in real-time
        this.#dispatchCartUpdateEvent(cart, expectedBaseItems);

        // Check if stable
        if (cart.item_count === lastItemCount) {
          stableChecks++;
        } else {
          stableChecks = 0;
        }
        lastItemCount = cart.item_count;

        // Stop if: we have expected items AND stable for 2 checks, OR timeout
        if ((cart.item_count >= expectedItemCount && stableChecks >= 2) || attempts >= maxAttempts) {
          resolve();
        } else {
          setTimeout(poll, 200);
        }
      };

      // Start polling immediately
      poll();
    });
  }

  /**
   * Adds variant to cart with preserved properties
   * @param {number} variantId - The variant ID to add
   * @returns {Promise<void>}
   */
  async #addToCart(variantId) {
    const formData = new FormData();
    // FormData.append expects string values
    formData.append('id', String(variantId));
    formData.append('quantity', '1');

    // Normalize and add all original properties
    if (this.#originalProperties) {
      const normalizedProps = this.#normalizeProperties(this.#originalProperties);
      Object.entries(normalizedProps).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, String(value));
      });
    }

    // Get cart sections to update
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    formData.append('sections', cartItemComponentsSectionIds.join(','));

    const fetchCfg = fetchConfig('javascript', { body: formData });

    // Use Theme global (defined in scripts.liquid)
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    });

    const data = await response.json();

    if (data.status || !response.ok) {
      throw new Error(data.message || data.description || 'Failed to add item');
    }

    // Dispatch cart update event
    document.dispatchEvent(
      new CartAddEvent({}, String(variantId), {
        source: 'size-selector-modal',
        itemCount: 1,
        sections: data.sections,
      })
    );
  }

  /**
   * Shows loading state
   * @returns {void}
   */
  #showLoading() {
    if (!this.refs.loadingState || !this.refs.sizesContainer) return;

    this.refs.loadingState.style.display = 'flex';
    this.refs.sizesContainer.style.display = 'none';
    this.refs.errorState.style.display = 'none';
  }

  /**
   * Hides loading state
   * @returns {void}
   */
  #hideLoading() {
    if (!this.refs.loadingState || !this.refs.sizesContainer) return;

    this.refs.loadingState.style.display = 'none';
    this.refs.sizesContainer.style.display = 'grid';
  }

  /**
   * Shows error state
   * @param {string} message - Error message
   * @returns {void}
   */
  #showError(message) {
    if (!this.refs.errorState || !this.refs.errorMessage) return;

    this.refs.errorMessage.textContent = message;
    this.refs.loadingState.style.display = 'none';
    this.refs.sizesContainer.style.display = 'none';
    this.refs.errorState.style.display = 'block';
  }

  /**
   * Resets modal state
   * @returns {void}
   */
  #resetState() {
    this.#currentLineItem = null;
    this.#productData = null;
    this.#originalProperties = null;
    this.#isProcessing = false;

    // Clean up event listeners
    this.#cleanupEventListeners();

    if (this.refs.sizesContainer) {
      this.refs.sizesContainer.innerHTML = '';
      this.refs.sizesContainer.classList.remove('loaded');
    }

    if (this.refs.errorState) {
      this.refs.errorState.style.display = 'none';
    }
  }

  /**
   * Lifecycle method called when element is removed from DOM
   * Ensures proper cleanup of resources
   * @returns {void}
   */
  disconnectedCallback() {
    // Cancel any pending requests
    this.#cancelPendingRequests();

    // Clean up event listeners
    this.#cleanupEventListeners();

    // Reset state
    this.#resetState();

    // Call parent disconnectedCallback if it exists
    if (super.disconnectedCallback) {
      super.disconnectedCallback();
    }
  }

  /**
   * Static method to clear the product cache (useful for testing or memory management)
   * @returns {void}
   */
  static clearCache() {
    SizeSelectorModal.#productCache.clear();
  }
}

if (!customElements.get('size-selector-modal')) {
  customElements.define('size-selector-modal', SizeSelectorModal);
}
